import Column from "@/atoms-react/column/Column.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Skeletton } from "@/atoms-react/skeletton/index.react";
import { Base } from "@/atoms-react/text/Text.react";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { useCityPostalCode } from "@/features/user/address/hooks/use-city-code";
import { useUser } from "@/features/user/hooks/use-user";
import type { AddressCreate, CityInfoType } from "@/features/user/types";
import { FAVOURITE_COUNTRY_CODES } from "@/features/utils/constants";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { type FormFieldGroup, getRealFieldGroup } from "@/features/utils/types";
import { XMarkIcon } from "@heroicons/react/24/solid";
import _, { debounce, DebouncedFunc } from "lodash";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
export type AddAddressFormProps = {
  address: AddressCreate;
  personNationality?: string[];
  fields?: FormFieldGroup<AddressCreate>;
  substep?: string;
  countryfirst?: boolean;
  noAutoComplete?: boolean;
  quiz?: boolean;
  residence?: boolean;
  isPrincipalAddress?: boolean;
  dataCy?: string;
  onChange: (address: AddressCreate, valid: boolean) => void;
} & Omit<React.ComponentPropsWithoutRef<"div">, "onChange">;

export const FormAddAddress = ({
  address,
  fields = {},
  personNationality,
  noAutoComplete,
  residence,
  onChange: _onChange = () => {},
  isPrincipalAddress,
  ...props
}: AddAddressFormProps) => {
  const { t } = useTranslation(["atoms"]);
  const { locale } = useLocale();
  const { useCountries } = useUser();
  const { data: countries, isLoading: isLoadingCountries } = useCountries(locale.language);
  const { auditPending, kycLevel } = useKYCContext();

  const realFields = getRealFieldGroup(Object.keys(address), fields);
  const { substep } = useParams();
  const countryDomTomId = [10, 110, 151, 100, 197, 155, 191, 195, 244, 102, 167, 103];
  const updateSpecificRule = (address: AddressCreate) => {
    if (realFields.zip) {
      if (!realFields.zip.required) {
        realFields.zip.required = countries?.countries.some(
          (a) => a.id === address.country && a.alpha2 === "FR"
        );
      }
    }
  };
  const checkValidity = (address: AddressCreate): boolean => {
    updateSpecificRule(address);
    const valid = !Object.keys(realFields).some((key) => {
      // is optional field
      if (key === "isPrincipalAddress") {
        return false;
      }
      const field = realFields[key as keyof AddressCreate];

      return field?.visible && field?.required && !address[key as keyof AddressCreate];
    });
    return valid;
  };
  const onChange = (address: AddressCreate) => {
    const valid = checkValidity(address);
    _onChange(address, valid);
  };
  const [suggestions, setSuggestions] = useState<CityInfoType[] | string[]>();
  const [selected, setSelected] = useState<boolean>(false);
  const [locked, setLocked] = useState<boolean>(false);
  const { getPostalCodeByCity } = useCityPostalCode();
  const { mutateAsync: getCityPostalCode } = getPostalCodeByCity();
  const debouncedSearch = useRef<DebouncedFunc<
    (data: { address: string }) => Promise<void>
  > | null>(null);

  useEffect(() => {
    debouncedSearch.current = debounce(async (data: { address: string }) => {
      const response = await getCityPostalCode(data);
      let filtredArray: CityInfoType[] = [];
      if (residence) {
        response?.map((ad: CityInfoType) => {
          ad.codesPostaux.map((cp: string) =>
            filtredArray.push({
              ...ad,
              codesPostal: cp
            })
          );
        });
      } else {
        filtredArray = response?.filter(
          (city) => city.nom.toLocaleLowerCase() === data.address.toLocaleLowerCase()
        );
      }

      filtredArray.length > 0
        ? setSuggestions(filtredArray)
        : response?.length > 0
          ? setSuggestions(response)
          : setSuggestions([t("molecules.add_address_form.no_match_city")]);
    }, 250);
    return () => debouncedSearch.current?.cancel();
  }, []);
  if (isLoadingCountries || !countries) return <Skeletton />;
  const handleInputChange = async (address: AddressCreate) => {
    if (locked) return;
    setSelected(false);
    const valid = checkValidity(address);
    address.zip = "";
    _onChange(address, valid);
    if (address.city.length === 0 || !address.city) {
      setSuggestions(undefined);
    }
    debouncedSearch.current?.({ address: address.city });
  };
  const onClickCity = (value: CityInfoType) => {
    const validCity = checkValidity({
      ...address,
      city: value.nom,
      zip: residence ? value.codesPostal : value.departement.code
    } as AddressCreate);
    address.city = value.nom;
    address.zip = residence ? (value.codesPostal as string) : value.departement.code;
    _onChange(address, validCity);
    setSelected(true);
    setLocked(true);
  };

  const onClear = () => {
    setLocked(false);
    address.city = "";
    address.zip = "";
    const validCity = checkValidity({
      ...address,
      city: "",
      zip: ""
    } as AddressCreate);
    _onChange(address, validCity);
  };

  const canUpdateMainAddress = kycLevel && kycLevel <= 1 && !auditPending;

  return (
    <Column
      className={twMerge("z-10 w-full", props.className)}
      {..._.omit(props, "onChange", "className")}
    >
      {(realFields?.tag ? !!realFields.tag.visible : true) && (
        <div className="mb-6 flex items-end">
          <InputLabel
            autoFocus={!realFields.tag?.disabled && props.autoFocus}
            value={address.tag}
            onChange={(value) => onChange({ ...address, tag: value })}
            label={realFields?.tag?.label ?? t("molecules.add_address_form.tag_label")}
            placeholder={realFields?.tag?.placeholder ?? t("molecules.add_address_form.tag_label")}
            required={realFields.tag?.required}
            disabled={realFields.tag?.disabled}
            noLatinCheck={true}
            className={"mr-4 basis-1/2"}
            dataCy="input-address-label"
          />
          <Checkbox
            dataTooltip={
              canUpdateMainAddress ? null : t("molecules.add_address_form.cant_update_main_address")
            }
            value={address.isPrincipalAddress}
            required={false}
            onChange={(value) => onChange({ ...address, isPrincipalAddress: value })}
            disabled={!canUpdateMainAddress}
            dataCy={`checkbox-main-address-${address.address}-${address.city}-${address.tag}`}
          >
            <Base className="mb-6 cursor-pointer">
              {t("molecules.add_address_form.is_principal_address")}
            </Base>
          </Checkbox>
        </div>
      )}

      {(realFields?.country ? !!realFields?.country.visible : true) && !!props.countryfirst && (
        <CountryInput
          dataCy={`select-address-country`}
          quiz={props?.quiz}
          substep={substep}
          address={address}
          fields={fields}
          onChange={onChange}
        />
      )}

      {(realFields?.address ? !!realFields?.address.visible : true) && (
        <div>
          <InputLabel
            autoFocus={!realFields.tag?.disabled && props.autoFocus}
            autoComplete="address-line1"
            value={address.address}
            onChange={(value) => onChange({ ...address, address: value })}
            label={realFields?.address?.label ?? t("molecules.add_address_form.address_line_label")}
            placeholder={
              realFields?.address?.placeholder ?? t("molecules.add_address_form.address_line_label")
            }
            required={realFields.address?.required}
            disabled={realFields.address?.disabled}
            dataCy={"input-address-1"}
          />
        </div>
      )}
      <div className={"mt-0"}>
        {(realFields?.country ? !!realFields?.country.visible : true) && !props.countryfirst && (
          <CountryInput
            dataCy={`select-address-country`}
            quiz={props?.quiz}
            address={address}
            fields={fields}
            onChange={(value) => {
              if (value.city) {
                value.city = "";
                value.zip = "";
                onClear();
              }
              onChange(value);
            }}
          />
        )}
      </div>
      <div className={props?.quiz === true ? "mt-0" : "mt-6"}>
        {countryDomTomId.includes(address.country as number) && !noAutoComplete ? (
          <>
            {(realFields?.city ? !!realFields?.city?.visible : !realFields.zip.visible) && (
              <div className="relative flex flex-col">
                <InputLabel
                  value={
                    address?.city?.length > 0 && address?.zip.length > 0
                      ? `${address.city} (${address.zip})`
                      : address.city
                  }
                  autoComplete="address-level2"
                  onChange={(value) => {
                    handleInputChange({ ...address, city: value });
                  }}
                  label={realFields?.city?.label ?? t("molecules.add_address_form.city_line_label")}
                  placeholder={
                    realFields?.city?.placeholder ?? t("molecules.add_address_form.city_line_label")
                  }
                  required={realFields.city?.required}
                  disabled={realFields.city?.disabled}
                />
                {suggestions && suggestions.length > 0 && !selected && (
                  <ul className="absolute top-20 z-50 mt-1 mt-2 max-h-40 w-full list-outside overflow-auto rounded-sm border-2 border-slate-50 bg-white font-semibold text-black duration-300 placeholder:font-semibold placeholder:text-slate-400 hover:list-inside hover:border-slate-100 focus:border-primary focus:bg-white dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:border-primary dark:focus:bg-slate-900 dark:hover:border-slate-800 dark:hover:bg-slate-900 dark:placeholder:text-slate-600">
                    {suggestions.slice(0, 30).map((city: CityInfoType | string, index) => {
                      if (typeof city === "string") {
                        return (
                          <li key={`${city}-${index}`} className="my-1 px-4 py-4">
                            {city}
                          </li>
                        );
                      } else {
                        // Assume city is of type CityInfoType
                        return (
                          <li
                            onClick={() => onClickCity(city)}
                            key={`${city.nom}-${city.codesPostal}-${city.departement.code}-${index}`}
                            className="my-1 cursor-pointer px-4 py-4 hover:rounded-sm hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-600"
                          >
                            {city.nom} ({residence ? city.codesPostal : city.departement.code})
                          </li>
                        );
                      }
                    })}
                  </ul>
                )}

                {locked && (
                  <XMarkIcon
                    onClick={onClear}
                    className="focus:pointer absolute top-1/2 right-4 h-6 w-6 translate-y-1 transform text-white-600 hover:text-white focus:outline-none"
                    aria-hidden="true"
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className={realFields.zip.visible ? "flex" : ""}>
              {(realFields?.zip ? !!realFields?.zip.visible : true) && (
                <InputLabel
                  value={address.zip}
                  autoComplete="postal-code"
                  className={"mr-4 grow"}
                  onChange={(value) => onChange({ ...address, zip: value })}
                  label={realFields?.zip?.label ?? t("molecules.add_address_form.zip_code_label")}
                  placeholder={
                    realFields?.zip?.placeholder ?? t("molecules.add_address_form.zip_code_label")
                  }
                  maxLength={10}
                  required={realFields.zip.required}
                  disabled={realFields.zip?.disabled}
                  dataCy="input-address-postcode"
                />
              )}
              {(realFields?.city ? !!realFields?.city.visible : true) && (
                <InputLabel
                  value={address.city}
                  autoComplete="address-level2"
                  className={"grow"}
                  onChange={(value) => onChange({ ...address, city: value })}
                  label={realFields?.city?.label ?? t("molecules.add_address_form.city_line_label")}
                  placeholder={
                    realFields?.city?.placeholder ?? t("molecules.add_address_form.city_line_label")
                  }
                  required={realFields.city?.required}
                  disabled={realFields.city?.disabled}
                  dataCy="input-address-city"
                />
              )}
            </div>
          </>
        )}
      </div>
    </Column>
  );
};

export const CountryInput = ({
  address,
  fields = {},
  substep,
  quiz,
  onChange = () => {},
  dataCy,
  ...props
}: Pick<
  AddAddressFormProps,
  "address" | "fields" | "onChange" | "substep" | "quiz" | "dataCy"
>) => {
  const { t } = useTranslation(["atoms"]);

  const { locale } = useLocale();
  const { useCountries } = useUser();
  const { data: countries } = useCountries(locale.language);
  const realFields = getRealFieldGroup(Object.keys(address), fields);
  return (
    <Dropdown
      dataCy={dataCy}
      label={realFields?.country?.label ?? t("molecules.add_address_form.country_label")}
      placeholder={
        realFields?.country?.placeholder ?? t("molecules.add_address_form.country_placeholder")
      }
      autoComplete="country"
      handleAutoComplete={(autoComplete, items, change) => {
        const country = (countries?.countries ?? []).find(
          (country) => country.alpha2.toLowerCase() === autoComplete.toLowerCase()
        );
        const autocompletedValue =
          items.find((item) => item?.key === country?.id.toString()) ?? null;

        if (autocompletedValue) change(autocompletedValue);
      }}
      onChange={(value) =>
        onChange(
          {
            ...address,
            city: substep === "birth" ? "" : address.city,
            country: parseInt(value.key)
          },
          true
        )
      }
      value={address.country?.toString()}
      //value={address.country?.toString()}
      groups={[{ key: "default" }]}
      items={
        (countries?.countries ?? []).map((country, index) => ({
          key: country.id.toString(),
          value: country.defaultName,
          icon: <Flag countryCode={country.alpha2} />,
          render: ({ key }) => (
            <div
              className={"flex w-full"}
              key={key}
              data-cy={dataCy ? dataCy + "-" + index : undefined}
            >
              <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
              <Base className={"ml-2"}>{country.defaultName}</Base>
            </div>
          ),
          groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
            ? "default"
            : undefined
        })) ?? []
      }
      className={quiz === true ? "mt-0" : "mt-6"}
      search={true}
      required={realFields.country?.required}
      disabled={realFields.country?.disabled}
      {...props}
    />
  );
};
